import { useState, useEffect, useMemo } from "react";
import { MdTrendingUp, MdFileDownload, MdHistory } from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const allRows = [headers, ...rows];
  const csv = allRows.map((r) => r.map(encode).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const roleLabel = (role) => {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "accounts") return "Accounts";
  if (role === "hr") return "HR";
  return "Staff";
};

const TaskActivityLogReport = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, uRes] = await Promise.all([
          api.get("/tasks/admin/all", { params: { reportsView: 1 } }),
          api.get("/users/list"),
        ]);
        setTasks(tRes.data.data || []);
        setUsers(uRes.data.data || []);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) load();
  }, [isAdmin]);

  const allUsers = useMemo(
    () => users.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const events = useMemo(() => {
    const rows = [];
    tasks.forEach((t) => {
      if (!t) return;
      // created
      if (t.createdAt && t.createdBy) {
        rows.push({
          time: new Date(t.createdAt),
          type: "created",
          taskId: t._id,
          taskTitle: t.title,
          actor: t.createdBy,
          details: "Task created",
        });
      }
      // assignment
      if (t.assignedAt && t.assignedTo) {
        rows.push({
          time: new Date(t.assignedAt),
          type: "assigned",
          taskId: t._id,
          taskTitle: t.title,
          actor: null, // assigner not stored; unknown
          details: `Assigned to ${t.assignedTo.name}`,
        });
      }
      // status / completion
      if (t.status && t.updatedAt && t.updatedAt !== t.createdAt) {
        const isDone = t.status === "done";
        rows.push({
          time: new Date(t.updatedAt),
          type: isDone ? "completed" : "status",
          taskId: t._id,
          taskTitle: t.title,
          actor: null,
          details: isDone
            ? "Task marked as completed"
            : `Status changed to ${t.status}`,
        });
      }
      // comments
      (t.comments || []).forEach((c) => {
        rows.push({
          time: c.createdAt ? new Date(c.createdAt) : new Date(),
          type: "comment",
          taskId: t._id,
          taskTitle: t.title,
          actor: c.user || null,
          details: c.text || "Comment",
        });
      });
    });
    return rows.sort((a, b) => b.time - a.time);
  }, [tasks]);

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (selectedType && e.type !== selectedType) return false;
      if (selectedUserId) {
        const actorId = e.actor?._id || e.actor;
        if (!actorId) return false;
        if (actorId !== selectedUserId) return false;
      }
      if (q) {
        const haystack = [
          e.taskTitle || "",
          e.actor?.name || "",
          e.details || "",
          e.type || "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [events, selectedType, selectedUserId, search]);

  if (!isAdmin) {
    return (
      <main className="page">
        <div className="card card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdTrendingUp size={26} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Task activity log
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                This report is available for admin users only.
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading activity log...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <MdTrendingUp style={{ marginRight: 6 }} />
            Task Activity Log
          </h1>
          <p className="page-subtitle">
            Tracks task creation, assignment, status changes, comments and
            completion to provide a full audit trail.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            exportToCsv(
              "task-activity-log.csv",
              ["Time", "Type", "Task", "Actor", "Details"],
              filteredEvents.map((e) => [
                e.time.toISOString(),
                e.type,
                e.taskTitle,
                e.actor?.name || "",
                e.details,
              ])
            )
          }
        >
          <MdFileDownload size={15} style={{ marginRight: 4 }} />
          Export Excel
        </button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="card-body"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          {/* Search first, spanning more width */}
          <div style={{ flex: 1, minWidth: 260, maxWidth: 420 }}>
            <label className="form-label">Search activity</label>
            <input
              className="form-control"
              placeholder="Search task, actor, details…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label">Filter by user</label>
            <select
              className="form-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">All users</option>
              {allUsers.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name} ({roleLabel(u.role)})
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 200 }}>
            <label className="form-label">Filter by activity type</label>
            <select
              className="form-control"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">All types</option>
              <option value="created">Task created</option>
              <option value="assigned">Task assigned</option>
              <option value="status">Status change</option>
              <option value="comment">Comment</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "flex-end",
            }}
          >
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Total events
              </div>
              <div className="stat-value">{events.length}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Filtered events
              </div>
              <div className="stat-value">{filteredEvents.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
            fontWeight: 700,
            fontSize: "0.9rem",
          }}
        >
          Activity log
        </div>
        {filteredEvents.length === 0 ? (
          <div className="empty-state">
            <MdHistory size={40} />
            <h3>No activity</h3>
            <p>No events match the current filters.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Task</th>
                  <th>Actor</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((e, idx) => (
                  <tr key={e.taskId + "-" + e.type + "-" + idx}>
                    <td className="text-muted">
                      {e.time.toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          e.type === "completed"
                            ? "badge-done"
                            : e.type === "comment"
                            ? "badge-review"
                            : "badge-todo"
                        }`}
                      >
                        {e.type}
                      </span>
                    </td>
                    <td>{e.taskTitle}</td>
                    <td className="text-muted">{e.actor?.name || "—"}</td>
                    <td className="text-muted">{e.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

export default TaskActivityLogReport;

